import { useMemo } from "react";

export type Entity<T> = {
  id: number;
  ancestry: number[];
  children?: T[];
};

interface FindEntityWithSubEntitiesResponse<T> {
  selectedEntity?: T;
  parentEntities: T[];
  orderedEntities: T[];
}

const getFlattenEntities = (e) =>
  e.children ? [e, ...e.children.flatMap(getFlattenEntities)] : e;

const useFindEntityWithSubEntities = <T extends Entity<T>>(
  entities: T[],
  entityId?: string,
): FindEntityWithSubEntitiesResponse<T> => {
  // すべてのカテゴリの一次元配列
  const allFlattenEntities = useMemo<T[]>(
    () => entities.flatMap((e) => getFlattenEntities(e)),
    [entities],
  );

  // 検索中のEntity
  const selectedEntity = useMemo<T | null>(() => {
    if (!entityId) return null;

    return allFlattenEntities.find((e) => e.id.toString() === entityId) || null;
  }, [allFlattenEntities, entityId]);

  // 検索中のEntityの親Entityの配列
  const parentEntities = useMemo<T[]>(() => {
    if (!selectedEntity) return [];

    if (selectedEntity.ancestry.length < 1) return [];

    return selectedEntity.ancestry.map((n) => {
      return allFlattenEntities.find((e) => n === e.id);
    });
  }, [selectedEntity, allFlattenEntities]);

  // 検索中のEntityと親子関係全て含めたEntityの配列
  const orderedEntities = useMemo(() => {
    if (!selectedEntity) return [];

    if (!parentEntities) return [selectedEntity];

    return [...parentEntities, selectedEntity];
  }, [parentEntities, selectedEntity]);

  return {
    selectedEntity,
    parentEntities,
    orderedEntities,
  };
};

export default useFindEntityWithSubEntities;
